import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { apiUrl } from '../../utils/apiBase';
import type { Flow, Card, Etapa, Colaborador } from '../../types';
import AIChart, { extractCharts, stripChartBlocks } from '../AIChart';

// ==================== TIPOS ====================
interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  loading?: boolean;
}

interface AIAssistantProps {
  flow: Flow;
  cards: Card[];
  etapas: Etapa[];
  colaboradores: Colaborador[];
  empresaId?: string;
}

// ==================== SUGESTÕES RÁPIDAS ====================
const QUICK_SUGGESTIONS = [
  { icon: '🏷️', label: 'Categoria de gastos', desc: 'Análise completa por categoria de compras', prompt: '__CATEGORIA_GASTOS__' },
];

const CATEGORIA_GASTOS_PROMPT = (categoria: string) => `[BUSCA_CATEGORIA: ${categoria}]

Faça uma VARREDURA COMPLETA e DETALHADA de TODOS os cards que contenham "${categoria}" no título, nos campos, no fornecedor, na descrição, na categoria de compra ou em qualquer informação do card.

Para CADA card encontrado, monte uma TABELA COMPLETA com as seguintes colunas:
| Card | Solicitante | Responsável | Fornecedor | Valor (R$) | Data da Compra | Prazo/Vencimento | Etapa Atual | Status |

Depois da tabela individual, apresente:

**📊 RESUMO DA CATEGORIA "${categoria}":**
- Total de cards encontrados
- Valor TOTAL gasto nesta categoria (soma de todos os cards)
- Valor médio por card
- Maior gasto (qual card e valor)
- Menor gasto (qual card e valor)
- Fornecedores envolvidos (lista com quantos cards cada)
- Solicitantes (quem mais pediu compras desta categoria)
- Cards com prazo vencido nesta categoria
- Distribuição por etapa (quantos em cada etapa)

Inclua um gráfico de barras com os valores por card desta categoria.

Se não encontrar cards exatos para "${categoria}", busque cards SIMILARES ou relacionados e informe. Seja abrangente na busca: procure em títulos, campos de texto, campos de categoria, fornecedor, qualquer campo que mencione "${categoria}" ou termos relacionados.

IMPORTANTE: NÃO omita nenhum card. Liste TODOS, mesmo que sejam muitos. A análise precisa ser completa e detalhada.`;

// ==================== COMPONENTE PRINCIPAL ====================
const AIAssistant = ({ flow, cards, etapas, colaboradores, empresaId }: AIAssistantProps) => {
  const [isOpen, setIsOpen] = useState(false);  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedContent, setExpandedContent] = useState<{ type: 'table' | 'chart'; html?: string; chart?: any } | null>(null);  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [awaitingCategoria, setAwaitingCategoria] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);
  const baseInputRef = useRef<string>('');  // Input antes de começar a gravar
  const transcriptRef = useRef<string>(''); // Acumula texto final reconhecido

  // Verificar suporte a Speech Recognition
  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null;
  const hasSpeech = !!SpeechRecognition;

  // Stats do fluxo (memo para não recalcular)
  const flowStats = useMemo(() => {
    let totalValor = 0;
    let atrasados = 0;
    const now = new Date();
    cards.forEach(c => {
      const campos = c.campos || {};
      Object.entries(campos).forEach(([key, val]) => {
        const kl = key.toLowerCase();
        if ((kl.includes('valor') || kl.includes('medio')) && typeof val === 'number' && val > 0) {
          totalValor += val;
        }
        if ((kl.includes('vencimento') || kl.includes('prazo')) && typeof val === 'string') {
          try {
            const dt = new Date(val);
            if (dt < now) atrasados++;
          } catch {}
        }
      });
    });
    return { totalValor, atrasados };
  }, [cards]);

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus no input quando abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Mensagem de boas-vindas ao abrir
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const valor = flowStats.totalValor;
      let welcome = `Olá! Sou o assistente inteligente do **Escalone**.\n\n`;
      welcome += `📋 **${flow.name}**\n`;
      welcome += `• ${cards.length} cards em ${etapas.length} etapas\n`;
      if (valor > 0) welcome += `• Valor total: **R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}**\n`;
      if (flowStats.atrasados > 0) welcome += `• ⚠️ ${flowStats.atrasados} cards com prazo vencido\n`;
      welcome += `\nEscolha uma sugestão abaixo ou me pergunte qualquer coisa! 👇`;
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: welcome,
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  // Enviar mensagem para a IA
  const sendMessage = useCallback(async (text?: string) => {
    let messageText = text || input.trim();
    if (!messageText || isLoading) return;

    // Se estava aguardando categoria, monta o prompt de análise por categoria
    let displayText = messageText;
    if (awaitingCategoria && !text) {
      displayText = `🏷️ Analisar categoria: ${messageText}`;
      messageText = CATEGORIA_GASTOS_PROMPT(messageText);
      setAwaitingCategoria(false);
    }

    const userMsg: AIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: displayText,
      timestamp: new Date()
    };

    const loadingMsg: AIMessage = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      loading: true
    };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setInput('');
    setIsLoading(true);

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = '44px';
    }

    try {
      // Construir mapa de definições de campos (id -> {nome, tipo})
      const campoDefsMap: Record<string, { nome: string; tipo: string }> = {};
      etapas.forEach(e => {
        (e.campos || []).forEach((campo: any) => {
          if (campo.id && campo.nome) {
            campoDefsMap[campo.id] = { nome: campo.nome, tipo: campo.tipo || 'texto' };
          }
        });
      });

      const cardContext = cards.map(c => {
        const etapa = etapas.find(e => e.id === c.etapaId);
        const responsavel = colaboradores.find(col => col.id === c.responsavelIndividualId);
        
        // Traduzir campos: de IDs crípticos para nomes legíveis com tipo
        const camposLegíveis: Record<string, any> = {};
        const camposRaw = c.campos || {};
        Object.entries(camposRaw).forEach(([campoId, val]) => {
          const def = campoDefsMap[campoId];
          if (def) {
            camposLegíveis[def.nome] = { valor: val, tipo: def.tipo };
          }
        });

        return {
          titulo: c.titulo,
          id: c.id,
          etapa: etapa?.nome || 'Desconhecida',
          etapaId: c.etapaId,
          campos: camposLegíveis,
          responsavel: responsavel?.nome || 'Não atribuído',
          criadoEm: c.createdAt,
          atualizadoEm: c.updatedAt,
          ehCardPai: c.ehCardPai,
          cardsFilhosIds: c.cardsFilhosIds || [],
          cardPaiId: c.cardPaiId || null,
          etapaHistory: (c.etapaHistory || []).map(h => ({
            etapaId: h.etapaId,
            movedAt: h.movedAt,
            leftAt: h.leftAt || null,
          })),
        };
      });

      const etapaContext = etapas.map(e => ({
        id: e.id,
        nome: e.nome,
        totalCards: cards.filter(c => c.etapaId === e.id).length,
      }));

      const response = await fetch(apiUrl('/ai/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          flowName: flow.name,
          flowId: flow.id,
          cards: cardContext,
          etapas: etapaContext,
          colaboradores: colaboradores.map(c => ({ id: c.id, nome: c.nome, cargo: c.cargo })),
          empresaId,
          history: messages.filter(m => !m.loading).slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      const data = await response.json();

      setMessages(prev => prev.map(m =>
        m.loading ? { ...m, content: data.response || 'Sem resposta.', loading: false, timestamp: new Date() } : m
      ));
    } catch (error) {
      console.error('[AIAssistant] Erro:', error);
      setMessages(prev => prev.map(m =>
        m.loading ? { ...m, content: '❌ Erro de conexão com o servidor.', loading: false, timestamp: new Date() } : m
      ));
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, isLoading, cards, etapas, colaboradores, flow, messages, empresaId, awaitingCategoria]);

  // Handler do teclado
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // Limpar conversa
  const clearChat = useCallback(() => {
    setMessages([]);
    setAwaitingCategoria(false);
    setTimeout(() => {
      setMessages([{
        id: 'welcome-new',
        role: 'assistant',
        content: `Conversa reiniciada. Como posso ajudar com o fluxo **"${flow.name}"**?`,
        timestamp: new Date()
      }]);
    }, 100);
  }, [flow.name]);

  // Extrair CSV de tabela markdown
  const extractCsvFromMarkdown = useCallback((text: string): string | null => {
    const lines = text.split('\n');
    const tableLines = lines.filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'));
    if (tableLines.length < 2) return null;
    // Remove separator line (| --- | --- |)
    const dataLines = tableLines.filter(l => !/^\|[\s\-:]+\|$/.test(l.trim()));
    if (dataLines.length < 2) return null;
    const csvRows = dataLines.map(line => {
      const cells = line.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1).map(c => c.trim());
      return cells.map(c => `"${c.replace(/"/g, '""')}"`).join(',');
    });
    // Add BOM for Excel UTF-8
    return '\uFEFF' + csvRows.join('\n');
  }, []);

  // Download CSV
  const downloadCsv = useCallback((csvContent: string, filename: string = 'dados_ia.csv') => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Renderizar markdown com botões de ampliar em tabelas
  const renderMarkdown = useCallback((text: string) => {
    // Strip chart blocks before rendering markdown
    let stripped = stripChartBlocks(text);
    // First handle code blocks (```...```)
    let result = stripped.replace(/```[\s\S]*?```/g, (match) => {
      const code = match.replace(/```\w*\n?/g, '').replace(/```/g, '').trim();
      return `<pre class="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-xs font-mono overflow-x-auto my-2 whitespace-pre-wrap">${code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
    });
    // Handle markdown tables
    const lines = result.split('\n');
    let inTable = false;
    const processed: string[] = [];
    let tableId = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('|') && line.endsWith('|')) {
        // Check if separator line
        if (/^\|[\s\-:]+(\|[\s\-:]+)*\|$/.test(line)) continue;
        const cells = line.split('|').filter((_, ci, arr) => ci > 0 && ci < arr.length - 1).map(c => c.trim());
        if (!inTable) {
          inTable = true;
          tableId++;
          processed.push(`<div data-table-group class="relative group/tbl my-2"><button data-expand-table="${tableId}" class="absolute -top-1 -right-1 z-10 opacity-0 group-hover/tbl:opacity-100 transition-opacity bg-violet-600 hover:bg-violet-700 text-white rounded-lg p-1 shadow-md" title="Ampliar tabela"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg></button><div class="overflow-x-auto"><table class="w-full text-xs border-collapse">`);
          processed.push('<thead><tr>' + cells.map(c => `<th class="border border-gray-200 dark:border-gray-600 bg-violet-50 dark:bg-violet-900/30 px-2 py-1.5 text-left font-semibold text-violet-700 dark:text-violet-300">${c}</th>`).join('') + '</tr></thead><tbody>');
        } else {
          processed.push('<tr>' + cells.map(c => `<td class="border border-gray-200 dark:border-gray-600 px-2 py-1 text-gray-600 dark:text-gray-300">${c}</td>`).join('') + '</tr>');
        }
      } else {
        if (inTable) {
          processed.push('</tbody></table></div></div>');
          inTable = false;
        }
        processed.push(line);
      }
    }
    if (inTable) processed.push('</tbody></table></div></div>');
    result = processed.join('\n');
    // Now apply inline markdown
    return result
      .replace(/&(?!amp;|lt;|gt;|quot;|#)/g, '&amp;')
      .replace(/<(?!\/?(?:strong|em|code|pre|div|table|thead|tbody|tr|th|td|br|span|svg|path|button)\b)/g, '&lt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-600 text-violet-600 dark:text-violet-300 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
      .replace(/^- (.*?)$/gm, '• $1')
      .replace(/\n/g, '<br/>');
  }, []);

  // Auto-resize do textarea
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  // Speech-to-Text (Web Speech API)
  const toggleListening = useCallback(() => {
    if (!SpeechRecognition) return;

    if (isListening) {
      // Parar gravação
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    // Salvar texto atual do input como base
    baseInputRef.current = input;
    transcriptRef.current = '';

    // Iniciar gravação
    const recognition = new SpeechRecognition();
    recognition.lang = 'pt-BR';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      // Reconstruir todo o transcript final + interim a partir dos resultados
      let allFinal = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          allFinal += event.results[i][0].transcript + ' ';
        } else {
          interim = event.results[i][0].transcript;
        }
      }
      transcriptRef.current = allFinal;
      // Montar input: base (antes de gravar) + texto reconhecido final + interim
      const base = baseInputRef.current;
      const sep = base && !base.endsWith(' ') ? ' ' : '';
      setInput((base + sep + allFinal + interim).replace(/  +/g, ' ').trim());
    };

    recognition.onerror = (event: any) => {
      console.error('[SpeechRecognition] Erro:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Permissão de microfone negada. Habilite nas configurações do navegador.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
      // Focar no input após parar
      setTimeout(() => inputRef.current?.focus(), 100);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, input, SpeechRecognition]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Formatar hora
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // ==================== RENDER DIRETO (sem sub-componentes para não perder foco) ====================
  const chatUI = createPortal(
    <>
      {/* ===== BOTÃO FLUTUANTE ===== */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={`
          fixed bottom-24 right-6 z-[9990] w-14 h-14 rounded-full
          flex items-center justify-center cursor-pointer select-none
          transition-all duration-300 ease-out
          ${isOpen
            ? 'bg-gray-800 hover:bg-gray-700 shadow-lg scale-90'
            : 'bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-500 hover:via-purple-500 hover:to-indigo-500 shadow-xl shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105'
          }
        `}
        title={isOpen ? 'Fechar IA' : 'Assistente IA'}
      >
        {isOpen ? (
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <>
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
              />
            </svg>
            {/* Badge indicador */}
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
          </>
        )}
      </button>

      {/* ===== PAINEL DE CHAT ===== */}
      {isOpen && (
        <div
          className={`fixed z-[9991] flex flex-col overflow-hidden
            bg-white dark:bg-gray-900
            border border-gray-200/80 dark:border-gray-700/80
            shadow-2xl shadow-black/10
            transition-all duration-300 ease-in-out
            ${isExpanded
              ? 'bottom-4 right-4 left-4 top-4 w-auto max-w-none rounded-2xl'
              : 'bottom-[160px] right-6 w-[440px] max-w-[calc(100vw-32px)] rounded-2xl'
            }`}
          style={isExpanded ? {} : { height: 'min(640px, calc(100vh - 190px))' }}
        >
          {/* ── Header ── */}
          <div className="shrink-0 px-5 py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 relative overflow-hidden">
            {/* Padrão decorativo */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white" />
              <div className="absolute -bottom-2 -left-2 w-16 h-16 rounded-full bg-white" />
            </div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-[15px] leading-tight">Escalone IA</h3>
                  <p className="text-white/60 text-xs mt-0.5 truncate max-w-[200px]">{flow.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 1 && (
                  <button
                    onClick={clearChat}
                    className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white/70 hover:text-white"
                    title="Nova conversa"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white/70 hover:text-white"
                  title={isExpanded ? 'Reduzir' : 'Ampliar'}
                >
                  {isExpanded ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => { setMessages([]); setAwaitingCategoria(false); setIsOpen(false); setIsExpanded(false); }}
                  className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white/70 hover:text-white"
                  title="Fechar"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Stats bar */}
            <div className="relative flex items-center gap-3 mt-3 pt-3 border-t border-white/15">
              <div className="flex items-center gap-1.5 text-white/80 text-xs">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <span>{cards.length} cards</span>
              </div>
              <div className="w-px h-3 bg-white/20" />
              <div className="flex items-center gap-1.5 text-white/80 text-xs">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
                <span>{etapas.length} etapas</span>
              </div>
              {flowStats.atrasados > 0 && (
                <>
                  <div className="w-px h-3 bg-white/20" />
                  <div className="flex items-center gap-1.5 text-amber-300 text-xs">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span>{flowStats.atrasados} atrasados</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Mensagens ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50/50 dark:bg-gray-800/30"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}
          >
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Avatar */}
                {msg.role === 'assistant' && (
                  <div className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mt-0.5 shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                )}
                {/* Balão */}
                <div className={`
                  max-w-[82%] rounded-2xl px-4 py-3 text-[13px] leading-[1.6]
                  ${msg.role === 'user'
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-tr-md shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-100 dark:border-gray-700 rounded-tl-md shadow-sm'
                  }
                `}>
                  {msg.loading ? (
                    <div className="flex items-center gap-3 py-0.5">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Analisando dados...</span>
                    </div>
                  ) : (
                    <>
                      <div
                        className="[&_strong]:text-violet-600 dark:[&_strong]:text-violet-400 [&_li]:ml-2 [&_table]:my-1 [&_br+br]:hidden"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                        onClick={(e) => {
                          const btn = (e.target as HTMLElement).closest('[data-expand-table]');
                          if (btn) {
                            const tableContainer = btn.closest('[data-table-group]');
                            if (tableContainer) {
                              const tableHtml = tableContainer.querySelector('table')?.outerHTML || '';
                              setExpandedContent({ type: 'table', html: tableHtml });
                            }
                          }
                        }}
                      />
                      {/* Gráficos com botão ampliar */}
                      {msg.role === 'assistant' && extractCharts(msg.content).map((chart, ci) => (
                        <div key={ci} className="relative group/chart inline-block">
                          <button
                            onClick={() => setExpandedContent({ type: 'chart', chart })}
                            className="absolute -top-1 -right-1 z-10 opacity-0 group-hover/chart:opacity-100 transition-opacity bg-violet-600 hover:bg-violet-700 text-white rounded-lg p-1 shadow-md"
                            title="Ampliar gráfico"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                            </svg>
                          </button>
                          <AIChart data={chart} />
                        </div>
                      ))}
                      {/* Botão Download CSV se tiver tabela */}
                      {msg.role === 'assistant' && extractCsvFromMarkdown(msg.content) && (
                        <button
                          onClick={() => {
                            const csv = extractCsvFromMarkdown(msg.content);
                            if (csv) downloadCsv(csv);
                          }}
                          className="flex items-center gap-1.5 mt-2 px-3 py-1.5 text-[11px] font-medium
                            bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300
                            border border-emerald-200 dark:border-emerald-700 rounded-lg
                            hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-all"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                          </svg>
                          Baixar CSV
                        </button>
                      )}
                      <div className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/50 text-right' : 'text-gray-400 dark:text-gray-500'}`}>
                        {formatTime(msg.timestamp)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Sugestões rápidas ── */}
          {messages.length <= 1 && (
            <div className="shrink-0 px-5 py-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="h-px flex-1 bg-gradient-to-r from-violet-200 to-transparent dark:from-violet-800" />
                <p className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-widest">Sugestões inteligentes</p>
                <div className="h-px flex-1 bg-gradient-to-l from-violet-200 to-transparent dark:from-violet-800" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (s.prompt === '__CATEGORIA_GASTOS__') {
                        setAwaitingCategoria(true);
                        setMessages(prev => [...prev, {
                          id: `cat-ask-${Date.now()}`,
                          role: 'assistant',
                          content: '🏷️ **Análise por Categoria de Gastos**\n\nQual categoria de gastos deseja analisar?\n\nDigite no campo abaixo, por exemplo:\n- *café*\n- *material de escritório*\n- *equipamentos de TI*\n- *carimbo*\n- *manutenção*\n\nOu qualquer termo que identifique o tipo de gasto.',
                          timestamp: new Date()
                        }]);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      } else {
                        sendMessage(s.prompt);
                      }
                    }}
                    disabled={isLoading}
                    className={`flex flex-col items-start text-left px-3 py-2.5
                      ${i === 0 ? 'col-span-2 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/20 border-violet-200 dark:border-violet-700' : 'bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/80 border-gray-100 dark:border-gray-700'}
                      hover:from-violet-50 hover:to-purple-50 dark:hover:from-violet-900/20 dark:hover:to-purple-900/20
                      text-gray-600 dark:text-gray-300 rounded-xl transition-all duration-200
                      border
                      hover:border-violet-300 dark:hover:border-violet-700
                      hover:shadow-sm hover:shadow-violet-100 dark:hover:shadow-violet-900/20
                      disabled:opacity-40 disabled:cursor-not-allowed
                      group`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm group-hover:scale-110 transition-transform">{s.icon}</span>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 group-hover:text-violet-700 dark:group-hover:text-violet-400 transition-colors">{s.label}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight pl-6">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Input ── */}
          <div className="shrink-0 px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex items-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 focus-within:border-violet-400 dark:focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-500/20 transition-all px-4 py-1.5">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={awaitingCategoria ? "Digite a categoria: café, equipamentos, carimbo..." : "Pergunte algo sobre o fluxo..."}
                rows={1}
                className="flex-1 resize-none bg-transparent border-0 text-sm
                  focus:outline-none focus:ring-0
                  placeholder-gray-400 dark:placeholder-gray-500
                  text-gray-800 dark:text-gray-100
                  py-2 leading-5"
                style={{ minHeight: '36px', maxHeight: '120px' }}
              />
              {/* Botão Microfone */}
              {hasSpeech && (
                <button
                  onClick={toggleListening}
                  disabled={isLoading}
                  className={`
                    shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mb-0.5
                    transition-all duration-200 cursor-pointer relative
                    ${isListening
                      ? 'bg-red-500 text-white shadow-md shadow-red-500/30 hover:bg-red-600'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-violet-100 dark:hover:bg-violet-900/30 hover:text-violet-600 dark:hover:text-violet-400'
                    }
                    ${isLoading ? 'opacity-40 cursor-not-allowed' : ''}
                  `}
                  title={isListening ? 'Parar gravação' : 'Falar (microfone)'}
                >
                  {isListening && (
                    <span className="absolute inset-0 rounded-xl bg-red-400 animate-ping opacity-40" />
                  )}
                  <svg className="w-4 h-4 relative z-10" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    {isListening ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                    )}
                  </svg>
                </button>
              )}
              {/* Botão Enviar */}
              <button
                onClick={() => { if (isListening && recognitionRef.current) { recognitionRef.current.stop(); setIsListening(false); } sendMessage(); }}
                disabled={!input.trim() || isLoading}
                className={`
                  shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mb-0.5
                  transition-all duration-200
                  ${input.trim() && !isLoading
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-sm hover:shadow-md hover:scale-105 cursor-pointer'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }
                `}
                title="Enviar (Enter)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </button>
            </div>
            <div className="flex items-center justify-between mt-1.5 px-1">
              <span className="text-[10px] text-gray-400 dark:text-gray-600">
                {isLoading ? '⏳ Processando...' : isListening ? '🔴 Ouvindo... clique no mic para parar' : hasSpeech ? 'Enter enviar • 🎙️ Microfone disponível' : 'Enter para enviar • Shift+Enter nova linha'}
              </span>
              <span className="text-[10px] text-gray-300 dark:text-gray-700 font-medium">Escalone IA</span>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );

  // Modal de conteúdo expandido (tabela/gráfico)
  const expandedModal = expandedContent ? createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6"
      onClick={() => setExpandedContent(null)}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-[90vw] max-h-[90vh] overflow-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setExpandedContent(null)}
          className="absolute top-3 right-3 p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Fechar"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {expandedContent.type === 'table' && expandedContent.html && (
          <div
            className="[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm
              [&_th]:border [&_th]:border-gray-300 [&_th]:bg-violet-50 [&_th]:px-4 [&_th]:py-2.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-violet-700
              [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-2 [&_td]:text-gray-700
              dark:[&_th]:border-gray-600 dark:[&_th]:bg-violet-900/30 dark:[&_th]:text-violet-300
              dark:[&_td]:border-gray-600 dark:[&_td]:text-gray-300
              min-w-[400px]"
            dangerouslySetInnerHTML={{ __html: expandedContent.html }}
          />
        )}
        {expandedContent.type === 'chart' && expandedContent.chart && (
          <div className="flex items-center justify-center min-w-[500px] min-h-[350px]">
            <AIChart data={expandedContent.chart} compact={false} />
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return <>{chatUI}{expandedModal}</>;
};

export default AIAssistant;
